import getDayOfYear from "date-fns/get_day_of_year";
import addDays from "date-fns/add_days";
import getYear from "date-fns/get_year";
import addWeeks from "date-fns/add_weeks";
import subWeeks from "date-fns/sub_weeks";
import endOfWeek from "date-fns/end_of_week";
import getISOWeek from "date-fns/get_iso_week";
import format from "date-fns/format";
import clonedeep from "lodash.clonedeep";

/**
 * Calculate the data needed to power the x-axis of the graph before merging the values from the platform data.
 *
 * Exported for testing purposes
 *
 * return
 * {
 *  dayNumbers: "1-2018, 2-2018"
 *  graphData: { labels: ["1st Jan"], rawData: [{day: "1st Jan", dayOfMonth: "1", year: "2018", value: ""} ]
 * }
 *
 * @param {int} numberOfDays
 */
export const calculateStatDates = (numberOfDays, locale) => {
  const data = {
    dayNumbers: "",
    graphData: {
      labels: [],
      rawData: []
    }
  };

  //set the start date to be today minus the number of days we care about
  //we remove 1 as we want to include todays date
  const startDate = addDays(new Date(), -Math.abs(numberOfDays - 1));

  //e.g. 100-2018
  data.dayNumbers = `${getDayOfYear(startDate)}-${getYear(startDate)}`;
  const day = format(startDate, "Do MMM", {
    locale
  });
  data.graphData.rawData.push({
    day,
    dayOfMonth: parseInt(format(startDate, "D")),
    year: parseInt(format(startDate, "YYYY")),
    value: 0
  });
  data.graphData.labels.push(format(startDate, "Do MMM", {
    locale
  }));
  for (let i = 1; i < numberOfDays; i++) {
    const date = addDays(startDate, i);
    //e.g. 100-2018, 101-2018
    data.dayNumbers = `${data.dayNumbers},${getDayOfYear(date)}-${getYear(date)}`;
    const day = format(date, "Do MMM", {
      locale
    });
    const stat = {
      day,
      dayOfMonth: parseInt(format(date, "D")),
      year: parseInt(format(date, "YYYY")),
      value: 0
    };
    data.graphData.rawData.push(stat);
    data.graphData.labels.push(format(date, "Do MMM", {
      locale
    }));
  }
  return data;
};
export const calculateWeeklyStatDates = numberOfWeeks => {
  const data = {
    weekNumbers: "",
    graphData: {
      labels: [],
      rawData: []
    }
  };
  const startDate = subWeeks(endOfWeek(new Date(), {
    weekStartsOn: 1
  }), numberOfWeeks);
  //e.g. 7-2020

  for (let i = 0; i < numberOfWeeks; i++) {
    const date = addWeeks(startDate, i);
    //e.g. 1-2020, 2-2020
    let weekNumber = getISOWeek(date);
    // we add one as we actually want to include the current week
    weekNumber++;
    data.weekNumbers = `${data.weekNumbers},${weekNumber}-${getYear(date)}`;
    const week = format(date, "Do MMM");
    const stat = {
      week,
      weekNumber,
      year: parseInt(format(date, "YYYY")),
      value: 0
    };
    data.graphData.rawData.push(stat);
    data.graphData.labels.push(week);
  }
  data.weekNumbers = data.weekNumbers.slice(1);
  return data;
};

/**
 * Taks the pre-calculates stats data based on user selection and merges the stat data into it.
 *
 * Exported for testing purposes
 *
 * @param {object} stats
 * @param {object} platformStats
 * @param {number} numberOfDays
 */
export const mergePlatformStatData = (inputStats, platformStats, numberOfDays, dataSetLabel, metric, profile, lastWeeksStats, lastMonthsStats) => {
  var _platformStats$childr, _platformStats$childr2;
  const stats = clonedeep(inputStats);
  const todaysYear = getYear(new Date());
  const startDaysYear = getYear(addDays(new Date(), -Math.abs(numberOfDays - 1)));

  //if we don't have anything, return
  if (!platformStats) {
    return createDataSetFromStatData(stats, dataSetLabel, metric, profile, {}, {});
  }

  //if neither year (could be the same year) has any data return
  if (((_platformStats$childr = platformStats.children[todaysYear]) === null || _platformStats$childr === void 0 ? void 0 : _platformStats$childr.length) === 0 && ((_platformStats$childr2 = platformStats.children[startDaysYear]) === null || _platformStats$childr2 === void 0 ? void 0 : _platformStats$childr2.length) === 0) {
    return createDataSetFromStatData(stats, dataSetLabel, metric, profile, {}, {});
  }
  stats.hasData = true;
  stats.graphData.rawData.map(dataSetItem => {
    const stat = platformStats.children[dataSetItem.year].filter(platformStat => {
      return platformStat.stat === metric.value;
    });
    if (stat.length === 1 && stat[0][dataSetItem.dayOfMonth]) {
      dataSetItem.value = stat[0][dataSetItem.dayOfMonth];
    }
    if (stat.length === 1 && stat[0][dataSetItem.weekNumber]) {
      dataSetItem.value = stat[0][dataSetItem.weekNumber];
    }
  });
  return createDataSetFromStatData(stats, dataSetLabel, metric, profile, lastWeeksStats, lastMonthsStats);
};

/**
 * Helper method to convert stats data to a flat array ready to be plotted in a graph
 *
 * @param {object} stats
 * @param {string} dataSetLabel
 * @param {object} metric
 * @param {object} profile
 */
const createDataSetFromStatData = (stats, dataSetLabel, metric, profile, lastWeeksStats, lastMonthsStats) => {
  stats.graphData.datasets = [];
  const dataPoints = [];
  let total = 0;
  let valuesNotZero = 0;
  stats.graphData.rawData.map(rawDataItem => {
    if (rawDataItem.value !== 0) {
      valuesNotZero++;
    }
    total += parseFloat(rawDataItem.value);
    const formattedValue = metric.format(rawDataItem.value, profile);
    dataPoints.push(rawDataItem.value);
    rawDataItem.value = formattedValue;
  });
  const lastWeekTotal = lastWeeksStats[metric.value] ? lastWeeksStats[metric.value] : 0;
  const lastMonthsTotal = lastMonthsStats[metric.value] ? lastMonthsStats[metric.value] : 0;
  stats.graphData.datasets.push({
    data: dataPoints,
    label: dataSetLabel,
    total: metric.format(total, profile),
    average: metric.format(total / valuesNotZero, profile),
    lastWeekTotal: metric.format(lastWeekTotal, profile),
    lastMonthsTotal: metric.format(lastMonthsTotal, profile),
    metric
  });
  return stats;
};